import { AxiosResponse } from 'axios';
import groupBy from 'lodash-es/groupBy';
import mapKeys from 'lodash-es/mapKeys';
import mapValues from 'lodash-es/mapValues';
import sortBy from 'lodash-es/sortBy';
import sumBy from 'lodash-es/sumBy';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { groupByMultipleKeys } from 'src/helpers/groupByMultipleKeys';
import {
  IAnonymizedCheckin,
  IBulkCheckinTimer,
  ICheckin,
  ICheckinTimer,
  IResetCheckin,
} from 'src/interfaces/Checkin';
import { distanceKmFromId, enrichStatistic, timedNonDnfRows } from 'src/helpers/statisticExtras';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import { Gender } from 'src/common/constants/genders';
import { EAgeGroups, TDataByGenderByAge, TStatistic, TStatistics } from 'src/interfaces/Statistics';
import { IFormCheckin } from 'src/interfaces/Checkin';

const groupByCompetitionAndDistance = groupByMultipleKeys<IAnonymizedCheckin>(
  ['competition', 'distance'],
  ':',
);

interface ICheckinsState {
  checkins: ICheckin[];
  checkinsById?: { [key: string]: ICheckin };
  deleteCheckin: (id: string) => Promise<void | AxiosResponse<null>>;
  fetchCheckins: () => Promise<void | AxiosResponse<ICheckin[]>>;
  fetchCheckinsByCompetitionId: (
    year: string,
    id: string,
  ) => Promise<void | AxiosResponse<ICheckin[]>>;
  fetchStatistics: (editionId: string) => Promise<{
    checkinsByPrice?: {
      [key: string]: number;
    };
    statistics?: TStatistics;
    totalPrice?: number;
  }>;
  handleBulkTimer: (data: IBulkCheckinTimer) => Promise<void | AxiosResponse<unknown>>;
  handleTimer: (data: ICheckinTimer) => Promise<void | AxiosResponse<null>>;
  isLoadingCheckins: boolean;
  resetCheckins: () => void;
  resetCounter: (data: IResetCheckin) => Promise<void | AxiosResponse<null>>;
  saveCheckin: (checkin: IFormCheckin, id?: string) => Promise<void | ICheckin>;
  setDnf: (data: { id: string; dnf: boolean }) => Promise<void | ICheckin>;
}

const useCheckinsStore = create<ICheckinsState>(set => {
  let competitionFetchGeneration = 0;

  const getCheckins = async () => {
    return SECURE_API.get<ICheckin[]>(`checkins/`)
      .then(({ data }) => {
        set({
          checkins: data,
          checkinsById: mapKeys(data, 'id'),
          isLoadingCheckins: false,
        });
      })
      .catch((error: string) => {
        set({
          checkins: [],
          checkinsById: undefined,
          isLoadingCheckins: false,
        });
        console.log({ error });
      });
  };

  const getCheckinsByCompetitionId = async (editionId: string, competitionId: string) => {
    const fetchGeneration = ++competitionFetchGeneration;
    return SECURE_API.get<ICheckin[]>(`checkins/edition/${editionId}/competition/${competitionId}`)
      .then(({ data }) => {
        if (fetchGeneration !== competitionFetchGeneration) return;
        set({ checkins: data, isLoadingCheckins: false });
      })
      .catch((error: string) => {
        if (fetchGeneration !== competitionFetchGeneration) return;
        set({ checkins: [], isLoadingCheckins: false });
        console.log({ error });
      });
  };

  return {
    checkins: [],
    deleteCheckin: async id => {
      set({ isLoadingCheckins: true });
      let editionId: string | undefined;
      let competitionId: string | undefined;
      return SECURE_API.delete<
        number,
        AxiosResponse<{ editionId?: string; competitionId?: string; message: string }>
      >(`checkins/${id}`)
        .then(({ data }) => {
          editionId = data.editionId;
          competitionId = data.competitionId;
        })
        .finally(() => {
          if (editionId && competitionId) {
            getCheckinsByCompetitionId(editionId, competitionId);
          } else getCheckins();
        });
    },
    fetchCheckins: async () => {
      set({ isLoadingCheckins: true });
      return getCheckins();
    },
    fetchCheckinsByCompetitionId: async (editionId: string, competitionId: string) => {
      set({ isLoadingCheckins: true, checkins: [] });
      return getCheckinsByCompetitionId(editionId, competitionId);
    },
    fetchStatistics: async (editionId: string) => {
      set({ isLoadingCheckins: true });
      const { data: anonymizedCheckins } = await SECURE_API.get<IAnonymizedCheckin[]>(
        `checkins/results/${editionId}`,
      );

      const checkinsByPrice = Object.entries(groupBy(anonymizedCheckins, ac => ac.price)).reduce(
        (acc, [price, checkins]) => {
          return {
            ...acc,
            [price]: checkins.length,
          };
        },
        {},
      );

      const totalPrice = anonymizedCheckins.reduce((acc, curr) => acc + curr.price, 0);

      const anonymizedCheckinsByCompetitionAndDistance =
        groupByCompetitionAndDistance(anonymizedCheckins);

      const statistics = Object.entries(anonymizedCheckinsByCompetitionAndDistance).reduce(
        (acc, [key, checkins]) => {
          const checkinsByGender = groupBy(checkins, c => c.gender);

          const winnersByGender = mapValues(checkinsByGender, c => {
            const sortedByDuration = sortBy(timedNonDnfRows(c), 'duration');

            const firstPlace: IAnonymizedCheckin[] = [];
            const secondPlace: IAnonymizedCheckin[] = [];
            const thirdPlace: IAnonymizedCheckin[] = [];

            sortedByDuration.forEach((obj, i) => {
              switch (i) {
                case 0:
                  firstPlace.push(obj);
                  break;
                case 1:
                  secondPlace.push(obj);
                  break;
                case 2:
                  thirdPlace.push(obj);
                  break;

                default:
                  break;
              }
            });

            return {
              firstPlace,
              secondPlace,
              thirdPlace,
            };
          });

          const checkinsByGenderByAge = mapValues(checkinsByGender, c => groupBy(c, 'age'));
          const checkinsByGenderByAgeKeys = Object.keys(checkinsByGenderByAge).sort() as Gender[];

          const dataByGender = checkinsByGenderByAgeKeys.reduce((acc, gender) => {
            const group = checkinsByGender[gender] ?? [];
            const timed = timedNonDnfRows(group);
            const totalTime = sumBy(timed, 'duration');
            return {
              ...acc,
              [gender]: {
                ratio: group.length,
                totalTime,
                averageTime: timed.length > 0 ? totalTime / timed.length : 0,
              },
            };
          }, {});

          const dataByGenderByAge = checkinsByGenderByAgeKeys.reduce<TDataByGenderByAge>((acc, gender) => {
            return {
              ...acc,
              [gender]: Object.values(EAgeGroups).reduce<TDataByGenderByAge[typeof gender]>(
                (ageAcc, age) => {
                  const theseCheckins = checkinsByGenderByAge[gender]?.[age] ?? [];
                  const timed = timedNonDnfRows(theseCheckins);
                  const totalTime = sumBy(timed, 'duration');
                  return {
                    ...ageAcc,
                    [age]: {
                      bestTime: sortBy(timed, 'duration')[0]?.duration || 0,
                      averageTime: timed.length > 0 ? totalTime / timed.length : 0,
                      noOfParticipants: theseCheckins?.length || 0,
                    },
                  };
                },
                {} as TDataByGenderByAge[typeof gender],
              ),
            };
          }, {} as TDataByGenderByAge);

          const base = {
            dataByGender,
            checkinsByGenderByAgeKeys,
            dataByGenderByAge,
            winnersByGender,
          };

          const distanceKm = distanceKmFromId(
            checkins[0]?.distance,
            useSystemOptionsStore.getState().distancesById,
          );

          return {
            ...acc,
            [key]: enrichStatistic(
              checkins,
              base as Omit<TStatistic, 'summary' | 'finishTimeBuckets'>,
              distanceKm,
            ),
          };
        },
        {},
      );

      set({ isLoadingCheckins: false });

      return {
        totalPrice,
        checkinsByPrice,
        statistics,
      };
    },
    handleBulkTimer: async ({ id, distanceId, editionId, ...checkinData }: IBulkCheckinTimer) => {
      set({ isLoadingCheckins: true });
      return SECURE_API.put<unknown>(`checkins/handleTimer/bulk/${id}/${distanceId}`, checkinData)
        .then(() => {
          set({ checkins: [] });
        })
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          getCheckinsByCompetitionId(editionId, id);
        });
    },
    handleTimer: async ({ id, ...checkinData }: ICheckinTimer) => {
      set({ isLoadingCheckins: true });
      let editionId: string | undefined;
      let competitionId: string | undefined;
      return SECURE_API.put<ICheckin>(`checkins/handleTimer/${id}`, checkinData)
        .then(({ data }) => {
          editionId = data.edition;
          competitionId = data.competition;
          set({ checkins: [] });
        })
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          if (editionId && competitionId) {
            getCheckinsByCompetitionId(editionId, competitionId);
          } else getCheckins();
        });
    },
    isLoadingCheckins: false,
    resetCheckins: () => set({ checkins: [] }),
    resetCounter: async (data: IResetCheckin) => {
      set({ isLoadingCheckins: true });
      return SECURE_API.put<null>(`checkins/resetCounter`, data)
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          set({ isLoadingCheckins: false });
        });
    },
    saveCheckin: async (checkin: IFormCheckin, id?: string) => {
      set({ isLoadingCheckins: true });
      let editionId: string | undefined;
      let competitionId: string | undefined;
      const saveCheckinPromise = id
        ? SECURE_API.put<ICheckin>(`checkins/${id}`, checkin)
        : SECURE_API.post<ICheckin>(`checkins`, checkin);
      return saveCheckinPromise
        .then(({ data }) => {
          editionId = data.edition;
          competitionId = data.competition;
          return data;
        })
        .finally(() => {
          if (editionId && competitionId) {
            getCheckinsByCompetitionId(editionId, competitionId);
          } else getCheckins();
        });
    },
    setDnf: async ({ id, dnf }) => {
      set({ isLoadingCheckins: true });
      let editionId: string | undefined;
      let competitionId: string | undefined;
      return SECURE_API.put<ICheckin>(`checkins/${id}`, { dnf })
        .then(({ data }) => {
          editionId = data.edition;
          competitionId = data.competition;
          return data;
        })
        .catch((error: string) => {
          console.log({ error });
        })
        .finally(() => {
          if (editionId && competitionId) {
            getCheckinsByCompetitionId(editionId, competitionId);
          } else getCheckins();
        });
    },
  };
});

export default useCheckinsStore;
